import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import { networkService } from "../../config/axios";
import LoadingScreen from "../../Elements/LoadingScreen/LoadingScreen";
import "./JourneyPage.css";

// Real SVG geometry (an explicit polyline), not a CSS border-corner hack --
// the border trick only sets border-right/border-bottom, leaving the other
// two sides at 0 width, so the box is asymmetric and a 180deg rotation
// (collapsed vs expanded) doesn't mirror around the same visual center.
// An SVG path has an exact, symmetric coordinate system, so rotating the
// whole 10x10 viewBox is pixel-exact in both states.
const ChevronIcon = () => (
    <svg className="journeyPage__chevron" viewBox="0 0 10 10" width="10" height="10" aria-hidden="true">
        <polyline points="1.5,3 5,6.5 8.5,3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DIRECTION_LABEL = {
    start: "Opened",
    forward: "Continued →",
    backward: "← Went back",
    repeat: "Stayed on page",
};

const CONFIDENCE_CLASS = {
    high: "",
    worth_probing: "journeyPage__trCard--soft",
    corroborated: "journeyPage__trCard--pos",
};

const CONFIDENCE_LABEL = {
    high: "High confidence",
    worth_probing: "Worth probing",
    corroborated: "Corroborated",
};

const VERDICT_LABEL = {
    act_now: "Act now",
    worth_a_look: "Worth a look",
    hold: "Hold",
    disqualified: "Disqualified",
    not_actionable: "Not actionable",
};

const reasonText = (reason) => (reason ? reason.replace(/_/g, " ") : null);

const TRAJECTORY_ICON = {
    rising: "↑",
    falling: "↓",
    flat: "→",
};

const formatDuration = (totalSeconds) => {
    if (totalSeconds === null || totalSeconds === undefined) return "—";
    const total = Math.round(totalSeconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const daysBetween = (firstIso, lastIso) => {
    if (!firstIso || !lastIso) return null;
    return Math.max(1, Math.round((new Date(lastIso) - new Date(firstIso)) / 86400000));
};

const sessionSpanText = (context) => {
    const sessionCount = context?.sessions?.length;
    if (!sessionCount) return null;
    const sessionWord = sessionCount === 1 ? "session" : "sessions";
    const firstVisitAt = context.sessions[0]?.started_at;
    const lastVisitAt = context.sessions[sessionCount - 1]?.started_at;
    const days = daysBetween(firstVisitAt, lastVisitAt);
    if (!days || days <= 1) return `${sessionCount} ${sessionWord}`;
    return `${sessionCount} ${sessionWord} over ${days} days`;
};

const locationText = (session) => {
    if (!session) return null;
    const parts = [session.city, session.country].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
};

const trustNoteText = (context) => {
    if (!context?.readiness) return null;
    const { identity_tier: identityTier, fragmentation_flag: fragmentationFlag } = context.readiness;
    const identityPart = identityTier === "confirmed"
        ? "identity confirmed via a single-recipient opt-in"
        : identityTier === "inferred"
            ? "identity inferred from a shared/group link, not directly confirmed"
            : "identity unresolved — this contact has not opted in";
    const consistencyPart = fragmentationFlag
        ? "inconsistent re-answers across visits — may be more than one person"
        : "consistent behavior across visits, no evidence of forwarding";
    return `${identityPart} · ${consistencyPart}`;
};

const formatGap = (fromIso, toIso) => {
    const diffMs = new Date(toIso) - new Date(fromIso);
    if (!(diffMs > 0)) return null;
    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    if (days > 0) return `${days} day${days === 1 ? "" : "s"}${hours ? `, ${hours} hour${hours === 1 ? "" : "s"}` : ""}`;
    if (hours > 0) return `${hours} hour${hours === 1 ? "" : "s"}${mins ? `, ${mins} min` : ""}`;
    return `${mins} min`;
};

// Bar width is capped and scaled against a fixed 3-minute reference (not the
// session's own max) so one long outlier page doesn't stretch every other
// bar in the session down to a sliver -- keeps each bar's length legible on
// its own.
const DWELL_BAR_REFERENCE_SECONDS = 180;
const dwellBarWidth = (dwellSeconds) => {
    const pct = ((dwellSeconds || 0) / DWELL_BAR_REFERENCE_SECONDS) * 100;
    return Math.min(100, Math.max(4, Math.round(pct)));
};

const JourneyPage = () => {
    // Read from the URL, not props: props are frozen at first page load and
    // do not update on client-side React Router navigation (e.g. the "View
    // new Journey" button on the old per-receiver stats page), so reading
    // identity from props here would show stale or missing data after any
    // client-side nav into this route.
    const { token, receiverId } = useParams();

    const [report, setReport] = useState(null);
    const [receiver, setReceiver] = useState(null);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        if (!token || !receiverId) {
            setLoading(false);
            return undefined;
        }

        mountedRef.current = true;
        setLoading(true);
        setError(null);

        networkService(`/journey_reports/for_recipient.json?doc_token=${token}&recipient_id=${receiverId}`, "GET")
            .then((response) => {
                if (!mountedRef.current) return;
                setReport(response?.data?.journey_report ?? null);
                setReceiver(response?.data?.receiver ?? null);
                setContext(response?.data?.context ?? null);
            })
            .catch((err) => {
                if (!mountedRef.current) return;
                setError(err?.response?.data?.error || "Couldn't load the journey report. Please try again later.");
            })
            .finally(() => {
                if (mountedRef.current) setLoading(false);
            });

        return () => { mountedRef.current = false; };
    }, [token, receiverId]);

    if (loading) return <LoadingScreen />;

    if (!token || !receiverId) {
        return <div className="journeyPage__empty">Missing document or recipient.</div>;
    }

    if (error) {
        return <div className="journeyPage__empty">{error}</div>;
    }

    const readiness = context?.readiness;
    const latestSession = context?.sessions?.length ? context.sessions[context.sessions.length - 1] : null;
    const action = report?.narrative?.recommended_action;

    const answersByPage = {};
    (context?.answers || []).forEach((a) => {
        const key = `${a.session_id}-${a.page}`;
        if (!answersByPage[key]) answersByPage[key] = [];
        answersByPage[key].push(a);
    });

    const timelineNotes = report?.narrative?.timeline_notes || [];
    const notesByPage = {};
    timelineNotes.forEach((note) => {
        const key = `${note.session_id}-${note.page}`;
        if (!notesByPage[key]) notesByPage[key] = [];
        notesByPage[key].push(note);
    });
    const signalTags = [...new Set(timelineNotes.map((n) => n.tag).filter(Boolean))];

    // Purely derived from real timestamps already in context -- no LLM
    // involvement, so no risk of an invented interpretation. Finds the first
    // page in a session entered at or after the opt-in moment, so a "Gate"
    // divider can be drawn at the right point in that session's timeline.
    const optinPageKeyForSession = (session) => {
        const completedAt = context?.optin?.completed_at;
        if (!completedAt) return null;
        const completedTime = new Date(completedAt).getTime();
        const gatePage = session.pages.find((p) => p.entered_at && new Date(p.entered_at).getTime() >= completedTime);
        return gatePage ? `${session.id}-${gatePage.page}` : null;
    };
    const sessionEndedWithoutOptin = (session) => {
        const completedAt = context?.optin?.completed_at;
        if (!completedAt) return true;
        return new Date(session.ended_at).getTime() < new Date(completedAt).getTime();
    };

    return (
        <div className="journeyPage">
            {/* Hero: who this is, on what KDF, and the deterministic readiness read.
                The band below is ONE aggregate score computed across every session
                (Navigator::ReadinessCalculator) -- not a bar per session. Per-session
                detail lives in the timeline accordion further down. */}
            <div className="journeyPage__hero">
                <div>
                    <div className="journeyPage__name">{receiver?.name || receiver?.email || "Recipient"}</div>
                    <div className="journeyPage__role">
                        {context?.meta?.kdf?.title && <>Journey on <b>{context.meta.kdf.title}</b></>}
                        {locationText(latestSession) && ` · ${locationText(latestSession)}`}
                        {sessionSpanText(context) && ` · ${sessionSpanText(context)}`}
                    </div>
                    <div className="journeyPage__chips">
                        {readiness?.identity_tier === "confirmed" && <span className="journeyPage__chip journeyPage__chip--id">Identity confirmed</span>}
                        {readiness?.tier === "hot" && <span className="journeyPage__chip journeyPage__chip--hot">Act now</span>}
                        {readiness?.trajectory && <span className="journeyPage__chip">{readiness.trajectory}</span>}
                    </div>
                </div>

                {readiness?.score !== null && readiness?.score !== undefined && (
                    <div>
                        <div className="journeyPage__bandLabel">Readiness</div>
                        <div className="journeyPage__bandVal">
                            {readiness.band_low}–{readiness.band_high}%
                            {readiness.trajectory && (
                                <span className={`journeyPage__bandTrend journeyPage__bandTrend--${readiness.trajectory}`}>
                                    {" "}{TRAJECTORY_ICON[readiness.trajectory] || ""} {readiness.trajectory}
                                </span>
                            )}
                        </div>
                        <div className="journeyPage__meter">
                            <div
                                className="journeyPage__meterFill"
                                style={{ left: `${readiness.band_low}%`, width: `${Math.max(readiness.band_high - readiness.band_low, 2)}%` }}
                            />
                        </div>
                        <div className="journeyPage__meterScale"><span>0%</span><span>100%</span></div>
                        {trustNoteText(context) && (
                            <div className="journeyPage__idnote"><b>Why we trust this:</b> {trustNoteText(context)}</div>
                        )}
                    </div>
                )}
            </div>

            {!report && (
                <div className="journeyPage__empty journeyPage__empty--inline">
                    No AI analysis yet for {receiver?.name || receiver?.email || "this recipient"} — it generates once they visit and answer at least one question.
                </div>
            )}

            {/* Truth Layer -- the skill's interpretive read, on top of the real timeline below */}
            {report && (
                <>
                    <h2 className="journeyPage__sectionHeading">The Truth Layer</h2>

                    <div className="journeyPage__verdict">
                        {report.narrative?.verdict && (
                            <span className={`journeyPage__verdictBadge journeyPage__verdictBadge--${report.narrative.verdict}`}>
                                {VERDICT_LABEL[report.narrative.verdict] || report.narrative.verdict}
                            </span>
                        )}
                        {report.narrative?.headline && (
                            <span className="journeyPage__verdictHeadline">{report.narrative.headline}</span>
                        )}
                    </div>
                    {reasonText(report.narrative?.reason) && (
                        <p className="journeyPage__reason">Reason: {reasonText(report.narrative.reason)}</p>
                    )}

                    {report.narrative?.readiness_narrative && (
                        <p className="journeyPage__sub">{report.narrative.readiness_narrative}</p>
                    )}
                    {report.narrative?.confidence_note && (
                        <p className="journeyPage__confidenceNote">{report.narrative.confidence_note}</p>
                    )}
                    {report.narrative?.watch_out && (
                        <div className="journeyPage__watchOut"><b>Watch out:</b> {report.narrative.watch_out}</div>
                    )}

                    {signalTags.length > 0 && (
                        <div className="journeyPage__signalStrip">
                            {signalTags.map((tag) => <span className="journeyPage__signalChip" key={tag}>{tag}</span>)}
                        </div>
                    )}

                    <Accordion
                        className="journeyPage__accordion"
                        alwaysOpen
                        defaultActiveKey={(report.narrative?.findings || []).map((_, idx) => String(idx))}
                    >
                        {(report.narrative?.findings || []).map((finding, idx) => (
                            <Accordion.Item eventKey={String(idx)} key={idx} className={CONFIDENCE_CLASS[finding.confidence] || ""}>
                                <Accordion.Header>
                                    <div className="journeyPage__trHeader">
                                        <span className="journeyPage__trHl">{finding.headline}</span>
                                        <span className="journeyPage__trSev">{CONFIDENCE_LABEL[finding.confidence] || finding.confidence}</span>
                                        <ChevronIcon />
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    {finding.claimed ? (
                                        <div className="journeyPage__trGrid journeyPage__trGrid--3">
                                            <div className="journeyPage__trCell"><div className="k">Claimed</div><div className="v">{finding.claimed}</div></div>
                                            <div className="journeyPage__trCell"><div className="k">Did</div><div className="v">{finding.observed}</div></div>
                                            <div className="journeyPage__trCell"><div className="k">What it means</div><div className="v">{finding.interpretation}</div></div>
                                        </div>
                                    ) : (
                                        <div className="journeyPage__trGrid">
                                            <div className="journeyPage__trCell"><div className="k">Observed</div><div className="v">{finding.observed}</div></div>
                                            <div className="journeyPage__trCell"><div className="k">What it means</div><div className="v">{finding.interpretation}</div></div>
                                        </div>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </>
            )}

            {/* Deterministic page-by-page timeline -- real data, every session, not an LLM summary */}
            {context?.sessions?.length > 0 && (
                <>
                    <h2 className="journeyPage__sectionHeading">Page-by-page journey</h2>
                    <p className="journeyPage__sub">Every page, every choice, every hesitation — in order.</p>

                    <Accordion
                        className="journeyPage__accordion"
                        alwaysOpen
                        defaultActiveKey={context.sessions.map((_, idx) => String(idx))}
                    >
                        {context.sessions.map((session, sIdx) => {
                            const prevSession = sIdx > 0 ? context.sessions[sIdx - 1] : null;
                            const gap = prevSession ? formatGap(prevSession.started_at, session.started_at) : null;
                            const gatePageKey = optinPageKeyForSession(session);
                            const endedWithoutOptin = sessionEndedWithoutOptin(session);

                            return (
                                <React.Fragment key={session.id}>
                                    {gap && <div className="journeyPage__gapline">⏱ <b>{gap}</b> later</div>}
                                    <Accordion.Item eventKey={String(sIdx)}>
                                        <Accordion.Header>
                                            <div className="journeyPage__sessHeader">
                                                <span className="journeyPage__sessNum">{session.ordinal}</span>
                                                <span className="journeyPage__sessTitle">Session {session.ordinal} of {session.of}</span>
                                                <span className="journeyPage__sessMeta">
                                                    {formatDateTime(session.started_at)} · {[session.device, session.browser].filter(Boolean).join(" · ")}
                                                    {locationText(session) && ` · ${locationText(session)}`} · {formatDuration(session.total_seconds)} total
                                                </span>
                                                <span className="journeyPage__sessTrailing">
                                                    {session.backtrack_count > 0 && (
                                                        <span className="journeyPage__sessBacktrack">{session.backtrack_count} backtrack{session.backtrack_count === 1 ? "" : "s"}</span>
                                                    )}
                                                    <ChevronIcon />
                                                </span>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            {session.arrived_from && (
                                                <div className="journeyPage__k2k">
                                                    ↩ Arrived from <b>{session.arrived_from.doc_title}</b>
                                                    {session.arrived_from.link_name ? ` (${session.arrived_from.link_name})` : ""}
                                                    {session.arrived_from.from_page ? `, page ${session.arrived_from.from_page}` : ""}
                                                    {session.arrived_from.journey_url && (
                                                        <a href={session.arrived_from.journey_url} target="_blank" rel="noopener noreferrer">Open that journey</a>
                                                    )}
                                                </div>
                                            )}
                                            {session.pages.length === 0 ? (
                                                <div className="journeyPage__noData">No page activity recorded for this session.</div>
                                            ) : (
                                            <div className="journeyPage__pageVisits">
                                                {session.pages.map((visit, vIdx) => {
                                                    const pageKey = `${session.id}-${visit.page}`;
                                                    const pageAnswers = answersByPage[pageKey] || [];
                                                    const notes = notesByPage[pageKey] || [];
                                                    const isLastVisit = vIdx === session.pages.length - 1;
                                                    const cardClasses = [
                                                        "journeyPage__pageCard",
                                                        visit.direction === "backward" ? "journeyPage__pageCard--backward" : "",
                                                        notes.length > 0 ? "journeyPage__pageCard--key" : "",
                                                    ].filter(Boolean).join(" ");
                                                    return (
                                                        <React.Fragment key={vIdx}>
                                                            {gatePageKey === pageKey && (
                                                                <div className="journeyPage__gapline journeyPage__gapline--gate">◆ <b>Opted in</b> here</div>
                                                            )}
                                                            <div className={cardClasses}>
                                                                <div className="journeyPage__row">
                                                                    <div className="journeyPage__rowPage">
                                                                        Page {visit.page}
                                                                    </div>
                                                                    <div className="journeyPage__rowQa">
                                                                        {pageAnswers.length > 0 ? (
                                                                            pageAnswers.map((a, aIdx) => (
                                                                                <div className="journeyPage__qaItem" key={aIdx}>
                                                                                    <span className="journeyPage__qaQuestion">{a.question_title}</span>
                                                                                    <span className="journeyPage__qaAnswer">{a.question_answer}</span>
                                                                                    {a.recipient_own_words && <span className="journeyPage__qaAiNote">In their own words · {a.weighting_ai_estimated ? "AI-estimated weighting" : "AI weighting pending"}</span>}
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <span className="journeyPage__qaEmpty">—</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="journeyPage__rowDwell">
                                                                        <span className="journeyPage__rowDwellTime">{formatDuration(visit.seconds_on_page)}</span>
                                                                        {visit.seconds_on_page !== null && visit.seconds_on_page !== undefined && (
                                                                            <div className="journeyPage__rowDwellBar">
                                                                                <i style={{ width: `${dwellBarWidth(visit.seconds_on_page)}%` }} />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="journeyPage__rowDirection">
                                                                        <span className="journeyPage__rowDirectionBadge">
                                                                            {DIRECTION_LABEL[visit.direction] || (visit.direction ? visit.direction : "Answered")}
                                                                        </span>
                                                                        {visit.tags?.length > 0 && (
                                                                            <div className="journeyPage__rowTags">
                                                                                {visit.tags.map((tag, tIdx) => (
                                                                                    <span className="journeyPage__rowTag" key={tIdx}>{tag}</span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {notes.map((note, nIdx) => (
                                                                    <div className="journeyPage__tellNote" key={nIdx}>
                                                                        {note.tag && <span className="journeyPage__tellTag">{note.tag}</span>}
                                                                        {note.note}
                                                                    </div>
                                                                ))}
                                                                {isLastVisit && endedWithoutOptin && (
                                                                    <div className="journeyPage__exitBadge">Session ended here — no opt-in yet</div>
                                                                )}
                                                            </div>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                            )}
                                            {(session.continued_to || []).map((next, nIdx) => (
                                                <div className="journeyPage__k2k journeyPage__k2k--next" key={`k2k-${session.id}-${nIdx}`}>
                                                    <div>
                                                        ↪ Continued to <b>{next.doc_title}</b>
                                                        {next.from_page ? ` from page ${next.from_page}` : ""}
                                                        {` · ${next.pages.length} page${next.pages.length === 1 ? "" : "s"} · ${formatDuration(next.total_seconds)}`}
                                                        {next.journey_url && (
                                                            <a href={next.journey_url} target="_blank" rel="noopener noreferrer">Open that journey</a>
                                                        )}
                                                    </div>
                                                    {next.pages.length > 0 && (
                                                        <div className="journeyPage__k2kPages">
                                                            {next.pages.map((p, pIdx) => (
                                                                <span className="journeyPage__rowTag" key={pIdx}>Page {p.page} · {formatDuration(p.seconds_on_page)}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {next.answers.length > 0 && (
                                                        <div className="journeyPage__k2kAnswers">
                                                            {next.answers.map((a, aIdx) => (
                                                                <div className="journeyPage__qaItem" key={aIdx}>
                                                                    <span className="journeyPage__qaQuestion">{a.question_title}</span>
                                                                    <span className="journeyPage__qaAnswer">{a.question_answer}</span>
                                                                    {a.recipient_own_words && <span className="journeyPage__qaAiNote">In their own words · {a.weighting_ai_estimated ? "AI-estimated weighting" : "AI weighting pending"}</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </React.Fragment>
                            );
                        })}
                    </Accordion>
                </>
            )}

            {action?.urgency && (
                <div className="journeyPage__act">
                    <div className="journeyPage__actTop">
                        <span className="journeyPage__actBadge">{VERDICT_LABEL[report.narrative?.verdict] || readiness?.tier || "Action"}</span>
                        <span className="journeyPage__actTitle">{action.urgency}</span>
                    </div>
                    <p className="journeyPage__actWhy">{action.why}</p>
                    <div className="journeyPage__doDont">
                        <div>
                            <h4>Do</h4>
                            <ul>{(action.do_list || []).map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                        </div>
                        <div>
                            <h4>Don&apos;t</h4>
                            <ul>{(action.dont_list || []).map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                        </div>
                    </div>

                    {report.suggested_email && (
                        <div className="journeyPage__email">
                            <h4>Suggested first reply — draft, edit before sending</h4>
                            <pre>{report.suggested_email}</pre>
                        </div>
                    )}
                </div>
            )}

            {report && (
                <div className="journeyPage__meta">
                    Generated {formatDateTime(report.created_at)}
                    {report.source === "fallback" && (
                        <span className="journeyPage__fallbackNote"> · generated without AI narrative</span>
                    )}
                    {report.source === "guard" && (
                        <span className="journeyPage__fallbackNote"> · blocked before analysis — flagged as test/internal traffic</span>
                    )}
                </div>
            )}
        </div>
    );
};

export default JourneyPage;
